import { c as _c } from "react/compiler-runtime";
import { DateCell } from "@components/common/date-cell";
import { DemoTag } from "@components/common/demo-tag";
import { ProjectManagerDisplay } from "@components/project/project-manager-display";
import { PROJECTS_TABLE_COLUMN_LABELS } from "@components/table/projects/projects-table-constants";
import styles from "@components/table/projects/projects-table.module.css";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { Box, Stack } from "@mui-wrapper";
import { generateGroupDetailsRoute, generateProjectDetailsRoute } from "@router/router-helper";
import { withEllipsis } from "@styles/common-styles";
import { getProjectGroupId, getProjectGroupName, getProjectSize, isProjectDemo } from "@utils/project-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SHOW_ON_HOVER_CLASS } from "@utils/ui-utils";
import { groupsSelector } from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { FontWeights, Text, Tooltip } from "@flat-ui";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { isProjectsTableColumnField } from "@components/table/projects/projects-table-utils";
import { ProjectTableThumbnail } from "@components/table/projects/project-table-thumbnail";
import { ProjectActions } from "@components/common/project-actions";
import { UiUtils } from "@stellar/utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

/**
 * FLEX_WEIGHTS defines the relative flex-grow weights for resizable columns in the projects table.
 * Higher values make columns expand more when there is extra space.
 * Only columns listed here will have a custom flex value; others use the default.
 */
const FLEX_WEIGHTS = {
  name: 1
};

/**
 * FIXED_MAX_WIDTHS sets the maximum width (in pixels) for specific columns in the projects table.
 * This prevents columns from growing too wide, especially for columns with limited content.
 * Only columns listed here will have a maxWidth restriction.
 */
const FIXED_MAX_WIDTHS = {
  projectManager: 200,
  managerAvatar: 54,
  group: 200,
  created: 150,
  lastCaptured: 150,
  lastUpdated: 200,
  client: 200,
  size: 120,
  accessLevel: 120,
  tags: 120,
  options: 90
};

/**
 * FIXED_MIN_WIDTHS sets the minimum width (in pixels) for specific columns in the projects table.
 * This ensures important columns remain readable and do not shrink too much when space is limited.
 * Only columns listed here will have a minWidth restriction.
 */
const FIXED_MIN_WIDTHS = {
  name: 250,
  projectManager: 175,
  managerAvatar: 40,
  group: 150,
  created: 150,
  lastUpdated: 150,
  client: 150,
  lastCaptured: 150,
  size: 90,
  accessLevel: 120,
  tags: 90,
  options: 90
};
/**
 * @returns the columns definition to be used in the projects table
 */
export function useProjectsTableColumns(t0) {
  const $ = _c(38);
  const {
    companyId,
    contextType
  } = t0;
  const {
    canViewCompanyGroups
  } = useHasUserValidFeatureCompanyLevel();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    columnPreferences,
    visibleOrderedColumns
  } = useColumnPreferencesContext();
  const groups = useAppSelector(groupsSelector);
  let t1;
  if ($[0] !== groups) {
    t1 = new Map(groups.map(_temp));
    $[0] = groups;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const groupNamesById = t1;
  let t2;
  bb0: {
    switch (contextType) {
      case "active projects":
        {
          t2 = "project-list-view";
          break bb0;
        }
      case "archived projects":
        {
          t2 = "project-list-view";
          break bb0;
        }
      case "group projects":
        {
          t2 = "group-project-list-view";
          break bb0;
        }
    }
    t2 = undefined;
  }
  const projectMenuContext = t2;
  let t3;
  if ($[2] !== companyId || $[3] !== contextType || $[4] !== trackEvent) {
    t3 = {
      field: "name",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.name,
      flex: FLEX_WEIGHTS.name,
      maxWidth: FIXED_MAX_WIDTHS.name,
      minWidth: FIXED_MIN_WIDTHS.name,
      sortable: true,
      disableReorder: true,
      renderCell: params => <Stack direction="row" sx={{
        ...withEllipsis,
        alignItems: "center",
        gap: "0.625rem"
      }}><ProjectTableThumbnail thumbnailUrl={params.row.entity.thumbnailUrl} projectId={params.row.entity.id} /><Tooltip component="div" title={<var>{params.row.entity.name}</var>} sx={{
          ...withEllipsis,
          fontWeight: FontWeights.Bold
        }}><Link to={generateProjectDetailsRoute(companyId, params.row.entity.id)} onClick={e => {
            trackEvent({
              name: ProjectEvents.openProjectDetails,
              props: {
                projectId: params.row.entity.id,
                eventLocation: contextType,
                viewMode: "list"
              }
            });
            e.stopPropagation();
          }} className={styles["removed-underline"]}><var>{params.row.entity.name}</var></Link></Tooltip></Stack>
    };
    $[2] = companyId;
    $[3] = contextType;
    $[4] = trackEvent;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      field: "managerAvatar",
      headerName: "",
      flex: FLEX_WEIGHTS.managerAvatar,
      maxWidth: FIXED_MAX_WIDTHS.managerAvatar,
      minWidth: FIXED_MIN_WIDTHS.managerAvatar,
      align: "right",
      renderCell: _temp2,
      sortable: false
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== canViewCompanyGroups || $[8] !== companyId) {
    t5 = {
      field: "projectManager",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.projectManager,
      flex: FLEX_WEIGHTS.projectManager,
      maxWidth: FIXED_MAX_WIDTHS.projectManager,
      minWidth: FIXED_MIN_WIDTHS.projectManager,
      renderHeader: _temp3,
      renderCell: params_2 => <Box sx={{
        ...withEllipsis
      }}><ProjectManagerDisplay project={params_2.row.entity} companyId={companyId} canViewAllCompanyGroups={canViewCompanyGroups} /></Box>,
      sortable: false
    };
    $[7] = canViewCompanyGroups;
    $[8] = companyId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      field: "created",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.created,
      flex: FLEX_WEIGHTS.created,
      maxWidth: FIXED_MAX_WIDTHS.created,
      minWidth: FIXED_MIN_WIDTHS.created,
      sortable: true,
      renderHeader: _temp4,
      renderCell: _temp5
    };
    t7 = {
      field: "lastUpdated",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.lastUpdated,
      flex: FLEX_WEIGHTS.lastUpdated,
      maxWidth: FIXED_MAX_WIDTHS.lastUpdated,
      minWidth: FIXED_MIN_WIDTHS.lastUpdated,
      sortable: true,
      renderHeader: _temp6,
      renderCell: _temp7
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[12] !== canViewCompanyGroups || $[13] !== companyId || $[14] !== groupNamesById) {
    t8 = {
      field: "group",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.group,
      flex: FLEX_WEIGHTS.group,
      maxWidth: FIXED_MAX_WIDTHS.group,
      minWidth: FIXED_MIN_WIDTHS.group,
      renderHeader: _temp8,
      renderCell: params_8 => {
        const groupId = getProjectGroupId(params_8.row.entity);
        const tooltipContent = <Tooltip component="div" title={<var>{getProjectGroupName(params_8.row.entity, groupNamesById)}</var>}><var>{getProjectGroupName(params_8.row.entity, groupNamesById)}</var></Tooltip>;
        if (groupId && canViewCompanyGroups) {
          return <Link to={generateGroupDetailsRoute(companyId, groupId)} onClick={_temp9} className={styles["removed-underline"]}><Box sx={{
              ...withEllipsis
            }}>{tooltipContent}</Box></Link>;
        } else {
          return tooltipContent;
        }
      },
      sortable: false
    };
    $[12] = canViewCompanyGroups;
    $[13] = companyId;
    $[14] = groupNamesById;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t10;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      field: "accessLevel",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.accessLevel,
      flex: FLEX_WEIGHTS.accessLevel,
      maxWidth: FIXED_MAX_WIDTHS.accessLevel,
      minWidth: FIXED_MIN_WIDTHS.accessLevel,
      renderHeader: _temp0,
      renderCell: _temp1,
      sortable: false
    };
    t10 = {
      field: "tags",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.tags,
      flex: FLEX_WEIGHTS.tags,
      maxWidth: FIXED_MAX_WIDTHS.tags,
      minWidth: FIXED_MIN_WIDTHS.tags,
      renderCell: _temp10,
      sortable: false
    };
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[18] !== columnPreferences) {
    t11 = () => <FaroTableColumnVisibilityMenuIcon columnPreferences={columnPreferences} />;
    $[18] = columnPreferences;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== projectMenuContext) {
    t12 = params_12 => <Box component="div" sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "right",
      visibility: "hidden"
    }} className={SHOW_ON_HOVER_CLASS}><ProjectActions project={params_12.row.entity} contextType={projectMenuContext} /></Box>;
    $[20] = projectMenuContext;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t12) {
    t13 = {
      field: "options",
      type: "actions",
      align: "right",
      flex: FLEX_WEIGHTS.options,
      maxWidth: FIXED_MAX_WIDTHS.options,
      minWidth: FIXED_MIN_WIDTHS.options,
      disableReorder: true,
      renderHeader: t11,
      renderCell: t12,
      sortable: false
    };
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  let t15;
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      field: "client",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.client,
      flex: FLEX_WEIGHTS.client,
      maxWidth: FIXED_MAX_WIDTHS.client,
      minWidth: FIXED_MIN_WIDTHS.client,
      renderHeader: _temp11,
      renderCell: _temp12,
      sortable: false
    };
    t15 = {
      field: "size",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.size,
      flex: FLEX_WEIGHTS.size,
      maxWidth: FIXED_MAX_WIDTHS.size,
      minWidth: FIXED_MIN_WIDTHS.size,
      sortable: true,
      renderHeader: _temp13,
      renderCell: _temp14
    };
    t16 = {
      field: "lastCaptured",
      headerName: PROJECTS_TABLE_COLUMN_LABELS.lastCaptured,
      flex: FLEX_WEIGHTS.lastCaptured,
      maxWidth: FIXED_MAX_WIDTHS.lastCaptured,
      minWidth: FIXED_MIN_WIDTHS.lastCaptured,
      sortable: true,
      renderHeader: _temp15,
      renderCell: _temp16
    };
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t14 = $[25];
    t15 = $[26];
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t13 || $[29] !== t3 || $[30] !== t5 || $[31] !== t8) {
    t17 = {
      name: t3,
      managerAvatar: t4,
      projectManager: t5,
      created: t6,
      lastUpdated: t7,
      group: t8,
      accessLevel: t9,
      tags: t10,
      options: t13,
      client: t14,
      size: t15,
      lastCaptured: t16
    };
    $[28] = t13;
    $[29] = t3;
    $[30] = t5;
    $[31] = t8;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const columnsMap = t17;
  let t18;
  if ($[33] !== columnsMap || $[34] !== visibleOrderedColumns) {
    let t19;
    if ($[36] !== columnsMap) {
      t19 = header => columnsMap[header];
      $[36] = columnsMap;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    t18 = visibleOrderedColumns.filter(isProjectsTableColumnField).map(t19);
    $[33] = columnsMap;
    $[34] = visibleOrderedColumns;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const orderedColumns = t18;
  return orderedColumns;
}
function _temp16(params_18) {
  const lastCapturedAt = params_18.row.entity.lastCapturedAt;
  if (!lastCapturedAt) {
    return UiUtils.DASH;
  }
  return <DateCell date={lastCapturedAt} />;
}
function _temp15(params_17) {
  return <TableHeaderWithDraggableIcon title={params_17.colDef.headerName ?? ""} />;
}
function _temp14(params_16) {
  return <Text variant="bodyS" component="var">{getProjectSize(params_16.row.entity)}</Text>;
}
function _temp13(params_15) {
  return <TableHeaderWithDraggableIcon title={params_15.colDef.headerName ?? ""} />;
}
function _temp12(params_14) {
  return <Text variant="bodyS"><var>{params_14.row.entity.clientName || UiUtils.DASH}</var></Text>;
}
function _temp11(params_13) {
  return <TableHeaderWithDraggableIcon title={params_13.colDef.headerName ?? ""} />;
}
function _temp10(params_11) {
  return isProjectDemo(params_11.row.entity) ? <DemoTag /> : "";
}
function _temp1(params_10) {
  const accessLevel = params_10.row.entity.accessLevel;
  return FaroCoreApiUtils.Project.AccessLevelDisplayNames[accessLevel];
}
function _temp0(params_9) {
  return <TableHeaderWithDraggableIcon title={params_9.colDef.headerName ?? ""} />;
}
function _temp9(e_0) {
  e_0.stopPropagation();
}
function _temp8(params_7) {
  return <TableHeaderWithDraggableIcon title={params_7.colDef.headerName ?? ""} />;
}
function _temp7(params_6) {
  return <DateCell date={params_6.row.entity.modifiedAt} />;
}
function _temp6(params_5) {
  return <TableHeaderWithDraggableIcon title={params_5.colDef.headerName ?? ""} />;
}
function _temp5(params_4) {
  return <DateCell date={params_4.row.entity.createdAt} />;
}
function _temp4(params_3) {
  return <TableHeaderWithDraggableIcon title={params_3.colDef.headerName ?? ""} />;
}
function _temp3(params_1) {
  return <TableHeaderWithDraggableIcon title={params_1.colDef.headerName ?? ""} />;
}
function _temp2(params_0) {
  return <Box component="div" sx={{
    display: "flex",
    alignItems: "center",
    pr: "0px"
  }}><UserAvatarWithMenu member={params_0.row.entity.managers?.projectManager} /></Box>;
}
function _temp(t0) {
  const {
    id,
    name
  } = t0;
  return [id, name];
}